import { useStore } from "@nanostores/react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { dataStore, filteredValuesStore, setTypeValues, updateUrl } from "../../_utils/searchStore";
import type { Value } from "../../_utils/types";
import { Checkbox } from "./Checkbox";
import { Collapse } from "./Collapse";
import { MultiSelect } from "./MultiSelect";
import { drawerTypeStore } from "./store";

const months = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

type Props = {
  isDesktop?: boolean;
};
export const StartMonths = ({ isDesktop = false }: Props) => {
  const router = useRouter();
  const state = useStore(drawerTypeStore);
  const selectedStartMonths = useStore(filteredValuesStore("startMonths"));
  const [localStartMonths, setLocalStartMonths] = useState(selectedStartMonths);
  const selectedStartMonthsRef = useRef(selectedStartMonths);
  const data = useStore(dataStore);

  useEffect(() => {
    const isDiff = () => {
      const oldT = selectedStartMonthsRef.current.sort((a, b) => a.value.localeCompare(b.value));

      const newT = selectedStartMonths.sort((a, b) => a.value.localeCompare(b.value));

      if (oldT.length !== newT.length) return true;

      for (let index = 0; index < newT.length; index++) {
        if (oldT[index] !== newT[index]) return true;
      }

      return false;
    };

    if (isDiff()) {
      selectedStartMonthsRef.current = selectedStartMonths;
      setLocalStartMonths(selectedStartMonths);
    }
  }, [selectedStartMonths]);

  const handleChange = (newValue: Value, directUpdate = false) => {
    let newStartMonths: Array<Value> = [];
    if (localStartMonths.some((lt) => lt.value === newValue.value)) {
      newStartMonths = localStartMonths.filter((lt) => lt.value !== newValue.value);
    } else {
      newStartMonths = [...localStartMonths, newValue];
    }

    setLocalStartMonths(newStartMonths);

    if (directUpdate) {
      setTypeValues("startMonths", newStartMonths);
      updateUrl(router);
    }
  };

  const handleOpen = () => {
    drawerTypeStore.set(state === "startMonths" ? null : "startMonths");
  };

  const handleClickOutside = () => {
    setTypeValues("startMonths", localStartMonths);

    drawerTypeStore.set(null);
  };

  const handleValidate = () => {
    setTypeValues("startMonths", localStartMonths);
    updateUrl(router);
  };

  const handleClear = () => {
    setTypeValues("startMonths", []);
    updateUrl(router);
  };

  const startMonths = [
    ...selectedStartMonths
      .filter((s) => !data?.startMonths.some((t) => t.key === s.value))
      .map((s) => ({
        value: s.value,
      })),
    ...(data?.startMonths.map((t) => ({ value: t.key })) || []),
  ];

  return isDesktop ? (
    <MultiSelect
      placeholder="Mois de départ"
      type="startMonths"
      onChange={handleChange}
      onValidate={handleValidate}
      onClear={handleClear}
      localValues={localStartMonths}
      selectedValues={selectedStartMonths}
      values={months
        .filter((m) => startMonths.some((sm) => sm.value === m.toLowerCase()))
        .map((d) => ({ label: d, value: d.toLowerCase() }))}
      disableSearch
      disableSort
    />
  ) : (
    <Collapse
      open={state === "startMonths"}
      title="Mois de départ"
      onclick={handleOpen}
      onClickOutside={handleClickOutside}
      count={selectedStartMonths.length}
    >
      <div className="mt-5 grid sm:grid-cols-2 gap-[10px]">
        {months
          .filter((m) => startMonths.some((sm) => sm.value === m.toLowerCase()))
          .map((startMonth) => (
            <Checkbox
              key={startMonth}
              label={startMonth}
              checked={localStartMonths.some((lt) => lt.value === startMonth.toLowerCase())}
              onChange={() =>
                handleChange({
                  type: "startMonths",
                  value: startMonth.toLowerCase(),
                })
              }
            />
          ))}
      </div>
    </Collapse>
  );
};
